import React, { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { BaseScaleView, BaseStagePlugin, useActiveFrameId, useFramework } from '@/framework';
import { applyTextLayoutFlip } from '../../utils/bbox';
import { resolveRuntimeFlip } from '../../utils/layer';
import type { TextLayerSVGPreviewPlugin } from './manager';

const SVGPreviewNode = ({ svg, style }: { svg: SVGSVGElement; style: React.CSSProperties }) => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        // svg 默认 display: inline，底边会对齐父级继承 font-size/line-height 生成的文本基线。
        // 当 svg 高度小于该行基线以上的高度时（如小画布上的小字号文本），svg 会被整体下推、与图层位置错开，
        // 改为 block 后 svg 直接贴齐容器左上角，不再参与行内排版。
        svg.style.display = 'block';
        ref.current?.replaceChildren(svg);
    }, [svg]);

    return <div ref={ref} style={style} />;
};

export const TextLayerSVGPreview = React.memo(() => {
    const framework = useFramework();
    const manager = framework.service.registration.getInstance<TextLayerSVGPreviewPlugin>('TextLayerSVGPreviewPlugin');

    const previews = useSyncExternalStore(manager.subscribe, manager.getPreviews, manager.getPreviews);
    const activeFrameId = useActiveFrameId();

    // 多页画布下只渲染当前激活页的预览。
    //
    // 成因：Preview 生命周期与页面无关，只在收到 commit 且正式 drawable 就绪后才释放；而 BaseScaleView
    // 仅按图层自身 left/top 定位，不区分所属页面。文字编辑中直接切换页面时，Text Editor 退出事务需要
    // 先等待字体子集加载（变形文字还要等截图上传与图片加载）才会发出 commit，网速慢时这段时间内，
    // 上一页图层的 Preview 会按原坐标叠在新页面上，直到释放后才消失。Resize 等其它连续交互同理。
    //
    // 方案：渲染时按图层所属页面过滤，不改动 Preview 的 begin/end/release 流转与退出事务，切回原页面时
    // 若退出尚未完成，Preview 仍会继续遮住被隐藏的正式 drawable。无限画布同时展示所有画布，不能按页过滤；
    // 读不到所属页面时保持原行为，已删除图层的 Preview 由 flatLayer.onDelete 释放。
    const { edgelessCanvas, frame } = framework.service;
    const visiblePreviews = edgelessCanvas.isEdgelessMode
        ? previews
        : previews.filter(({ layer }) => {
              const frameId = frame.getLayerFrameId(layer.id);
              return frameId === undefined || frameId === activeFrameId;
          });

    if (visiblePreviews.length === 0) return null;

    return (
        <BaseStagePlugin name="text-layer-svg-preview" zIndex={3}>
            {visiblePreviews.map(({ layer, layout, svg }) => {
                const { flipH, flipV } = resolveRuntimeFlip(layer);
                const { renderBox, layoutBox } = applyTextLayoutFlip(layout, flipH, flipV);
                // BaseScaleView 原点是图层左上角；竖排 layout/render 坐标系以 -contentWidth 为原点。
                // SVG viewBox 已按 renderBox 归位，CSS 只需再叠加相对 layoutBox 的外扩偏移。
                const left = renderBox.left - layoutBox.left;
                const top = renderBox.top - layoutBox.top;

                return (
                    <BaseScaleView key={layer.id} layer={layer} style={{ opacity: layer.opacity, overflow: 'visible' }}>
                        <SVGPreviewNode
                            svg={svg}
                            style={{
                                position: 'absolute',
                                left,
                                top,
                                width: renderBox.width,
                                height: renderBox.height,
                                transform: flipH || flipV ? `scale(${flipH ? -1 : 1}, ${flipV ? -1 : 1})` : undefined,
                                transformOrigin: 'center',
                            }}
                        />
                    </BaseScaleView>
                );
            })}
        </BaseStagePlugin>
    );
});

TextLayerSVGPreview.displayName = 'TextLayerSVGPreview';
